/**
 * Deep links del lado del front.
 *
 * Los builders puros viven en `@ops/contracts/deeplinks` y **no se reimplementan
 * acá**. Este archivo hace una sola cosa: inyectar la config que llega de
 * `GET /api/config` para que ninguna pantalla hardcodee el dominio de Plane,
 * Close, Stripe, la plataforma o Search Console.
 *
 * La mayoría de los payloads ya traen su sobre `links` con los deep links
 * armados en el servidor. Estos builders son para lo que el front arma sin
 * payload de por medio (una query de SEO tipeada en un filtro, por ejemplo).
 *
 * Regla que no se negocia: si el id externo falta, el builder devuelve `null` y
 * la UI muestra "sin vincular" con la acción de vincular. Nunca un link roto.
 */
import {
  DEFAULT_DEEP_LINK_CONFIG,
  closeLead,
  closeOpportunity,
  deepLinkConfig,
  planeWorkItem,
  plataformaAgente,
  plataformaCliente,
  searchConsoleQuery,
  stripeCustomer,
  stripeSubscription,
  type DeepLink,
  type DeepLinkConfig,
  type PlaneWorkItemOptions,
} from '@ops/contracts'
import { createContext, useContext, useMemo } from 'react'

/** Contexto con la config del servidor. Sin provider cae en los defaults. */
export const DeepLinkConfigContext = createContext<DeepLinkConfig>(
  DEFAULT_DEEP_LINK_CONFIG,
)

export function useDeepLinkConfig(): DeepLinkConfig {
  return useContext(DeepLinkConfigContext)
}

interface NombreOpt {
  nombre?: string | null
}

/** Builders ya atados a la config del entorno. */
export interface DeepLinkBuilders {
  planeWorkItem: (
    workItemId: string | null,
    options?: PlaneWorkItemOptions,
  ) => DeepLink | null
  closeLead: (leadId: string | null, options?: NombreOpt) => DeepLink | null
  closeOpportunity: (
    leadId: string | null,
    opportunityId: string | null,
    options?: NombreOpt,
  ) => DeepLink | null
  stripeCustomer: (
    customerId: string | null,
    options?: NombreOpt,
  ) => DeepLink | null
  stripeSubscription: (
    subscriptionId: string | null,
    options?: NombreOpt,
  ) => DeepLink | null
  plataformaCliente: (
    clientId: string | null,
    options?: NombreOpt,
  ) => DeepLink | null
  plataformaAgente: (
    agentId: string | null,
    options?: NombreOpt,
  ) => DeepLink | null
  searchConsoleQuery: (query: string | null) => DeepLink | null
}

/** Ata todos los builders a una config concreta. Puro: testeable sin React. */
export function buildersFor(cfg: DeepLinkConfig): DeepLinkBuilders {
  return {
    planeWorkItem: (id, options) => planeWorkItem(cfg, id, options),
    closeLead: (id, options) => closeLead(cfg, id, options),
    closeOpportunity: (leadId, oppId, options) =>
      closeOpportunity(cfg, leadId, oppId, options),
    stripeCustomer: (id, options) => stripeCustomer(cfg, id, options),
    stripeSubscription: (id, options) => stripeSubscription(cfg, id, options),
    plataformaCliente: (id, options) => plataformaCliente(cfg, id, options),
    plataformaAgente: (id, options) => plataformaAgente(cfg, id, options),
    searchConsoleQuery: (query) => searchConsoleQuery(cfg, query),
  }
}

/** Los builders del entorno actual. Memoizados por config. */
export function useDeepLinks(): DeepLinkBuilders {
  const cfg = useDeepLinkConfig()
  return useMemo(() => buildersFor(cfg), [cfg])
}

/**
 * Normaliza lo que llega de `/api/config` a un `DeepLinkConfig` completo. Si el
 * servidor omite un sistema, se cae al default en vez de armar una URL a medias.
 */
export function configFromServer(
  parcial: Partial<DeepLinkConfig> | undefined | null,
): DeepLinkConfig {
  if (parcial === undefined || parcial === null) {
    return DEFAULT_DEEP_LINK_CONFIG
  }
  return deepLinkConfig(parcial)
}
